import { queryKeys } from '@/lib/query-keys';
import { phonePlanClient } from '@/lib/api/ts-rest';
import { createLogger } from '@/lib/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

// customPrice in cents; null clears the override (org falls back to base).
export function useUpdatePlanPrice(planId: string, organizationId: string) {
  const queryClient = useQueryClient();
  const log = createLogger('useUpdatePlanPrice');
  return useMutation({
    mutationFn: async (customPrice: number | null) => {
      const res = await phonePlanClient.updatePhonePlanCustomPrice({
        params: { organizationId, planId },
        body: { customPrice },
      });
      if (res.status !== 200) throw new Error('Failed to update price');
      return true;
    },
    onSuccess: (_data, customPrice) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.catalog.phonePlansByOrganization(organizationId),
      });
      toast.success(
        customPrice === null
          ? 'Prix personnalisé réinitialisé'
          : 'Prix personnalisé mis à jour'
      );
    },
    onError: (error) => {
      toast.error('Échec de la mise à jour du prix');
      log.error('Failed to update custom price', { error });
    },
  });
}

// Base (catalog) price in cents — global to every organization.
export function useUpdatePlanBasePrice(planId: string, organizationId: string) {
  const queryClient = useQueryClient();
  const log = createLogger('useUpdatePlanBasePrice');
  return useMutation({
    mutationFn: async (priceCents: number) => {
      const res = await phonePlanClient.updatePhonePlanBasePrice({
        params: { planId },
        body: { price: priceCents },
      });
      if (res.status !== 200) throw new Error('Failed to update base price');
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.catalog.phonePlansByOrganization(organizationId),
      });
      toast.success('Prix de base mis à jour (toutes les organisations)');
    },
    onError: (error) => {
      toast.error('Échec de la mise à jour du prix de base');
      log.error('Failed to update base price', { error });
    },
  });
}
